/**
 * Wrap localStorage and sessionStorage to conform to the Storage interface
 * will silently fail when SecurityError is thrown
 */
export class WebStorage implements Storage {
  private readonly _storage: Storage | null = null;

  private readonly _fallbackStore = new Map<string, string>();

  constructor(source: "localStorage" | "sessionStorage") {
    try {
      const storage = window[source];
      const value = storage.getItem("__dm_test__");
      storage.setItem("__dm_test__", value ?? "");
      this._storage = storage;
    } catch {
      // Storage is disabled
      this._storage = null;
    }
  }

  get available(): boolean {
    return this._storage !== null;
  }

  get length(): number {
    if (this._storage) {
      return this._storage.length;
    }
    return this._fallbackStore.size;
  }

  clear(): void {
    this._fallbackStore.clear();
    this._storage?.clear();
  }
  getItem(key: string): string | null {
    if (this._storage) {
      return this._storage.getItem(key);
    }
    if (this._fallbackStore.has(key)) {
      return this._fallbackStore.get(key)!;
    }
    return null;
  }
  key(index: number): string | null {
    if (this._storage) {
      return this._storage.key(index);
    }

    if (this._fallbackStore.size <= index) {
      return null;
    }

    return this._fallbackStore.keys().drop(index).next().value ?? null;
  }
  removeItem(key: string): void {
    this._fallbackStore.delete(key);
    this._storage?.removeItem(key);
  }
  setItem(key: string, value: string): void {
    this._fallbackStore.set(key, value);
    this._storage?.setItem(key, value);
  }
}

export const localStorage = /* @__PURE__ */ new WebStorage("localStorage");
export const sessionStorage = /* @__PURE__ */ new WebStorage("sessionStorage");
